import axios from 'axios';
import i18next from 'i18next';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // 添加语言头，使用i18next.language而不是localStorage
  const language = i18next.language || 'en';
  config.headers['Accept-Language'] = language;
  
  return config;
});

// Add response interceptor to handle unauthorized responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      // Clear localStorage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Redirect to login page if not already there
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// MongoDB ObjectId validation regex
const isValidObjectId = (id: string) => /^[0-9a-fA-F]{24}$/.test(id);

export const auth = {
  login: async (email: string, password: string) => {
    const formData = new URLSearchParams();
    formData.append('username', email);
    formData.append('password', password);
    
    const response = await api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });
    return response.data;
  },
  
  register: async (username: string, email: string, password: string) => {
    const response = await api.post('/auth/register', null, {
      params: { username, email, password }
    });
    return response.data;
  },
};

export const patients = {
  getAll: async () => {
    try {
      const response = await api.get('/patients');
      return response.data;
    } catch (error) {
      console.error('Error fetching patients:', error);
      throw error;
    }
  },
  
  getById: async (id: string) => {
    try {
      if (!isValidObjectId(id)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      const response = await api.get(`/patients/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching patient:', error);
      throw error;
    }
  },
  
  create: async (patientData: any) => {
    try {
      // Remove any id field if present for new patients
      const { id, ...dataToSend } = patientData;
      const response = await api.post('/patients', dataToSend);
      return response.data;
    } catch (error) {
      console.error('Error creating patient:', error);
      throw error;
    }
  },
  
  update: async (id: string, patientData: any) => {
    try {
      if (!isValidObjectId(id)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      // Ensure we're using the correct id
      const { id: patientId, ...dataToUpdate } = patientData;
      const response = await api.put(`/patients/${id}`, {
        ...dataToUpdate,
        id: id
      });
      return response.data;
    } catch (error) {
      console.error('Error updating patient:', error);
      throw error;
    }
  },
  
  delete: async (id: string) => {
    try {
      if (!isValidObjectId(id)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      await api.delete(`/patients/${id}`);
      return true;
    } catch (error) {
      console.error('Error deleting patient:', error);
      return false;
    }
  },
};

export const aiChat = {
  sendMessage: async (patientId: string, message: string) => {
    try {
      if (!isValidObjectId(patientId)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      
      // Get the selected model from localStorage as we can't use hooks in regular functions
      const selectedModel = localStorage.getItem('selectedModel') || 'deepseek';
      
      const messageData = {
        message_id: Date.now().toString(),
        content: message,
        timestamp: new Date(),
        model: selectedModel
      };
      
      const response = await api.post(`/ai/chat/${patientId}`, messageData);
      return response.data;
    } catch (error) {
      console.error('Error sending message to AI:', error);
      throw error;
    }
  },
  
  sendMessageStream: (
    patientId: string,
    content: string,
    onChunk: (chunk: { content?: string; reasoning_content?: string }) => void,
    onComplete: (messageId: string) => void,
    onError: (error: string) => void,
    mode: string = 'medical'
  ) => {
    // Get the selected model from localStorage
    const selectedModel = localStorage.getItem('selectedModel') || 'deepseek';
    
    // Create a message object
    const message = {
      content: content,
      message_id: Date.now().toString(),
      model: selectedModel,
      mode: mode
    };

    // Create the EventSource for SSE
    const url = `${api.defaults.baseURL}/ai/chatstream/${patientId}`;
    
    // Create a new AbortController
    const controller = new AbortController();
    const { signal } = controller;
    
    // 获取当前语言设置
    const language = i18next.language || 'en';
    
    // Use fetch with POST and request body
    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Accept-Language': language
      },
      body: JSON.stringify({ message }),
      signal
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Create a reader from the response body stream
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      
      // Function to process the stream
      function processStream(): Promise<void> {
        return reader.read().then(({ done, value }) => {
          if (done) {
            return;
          }
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n\n');
          
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.substring(6));
                
                if (data.error) {
                  onError(data.content || 'Unknown error');
                  return;
                }
                
                // Handle both content and reasoning_content
                onChunk({
                  content: data.content || '',
                  reasoning_content: data.reasoning_content || ''
                });
                
                if (data.done) {
                  onComplete(data.message_id);
                  return;
                }
              } catch (e) {
                console.error('Error parsing SSE data:', e);
              }
            }
          }
          
          return processStream();
        });
      }
      
      processStream().catch(error => {
        onError(error.message);
      });
    })
    .catch(error => {
      if (error.name !== 'AbortError') {
        onError(error.message);
      }
    });
    
    // Return a function to close the stream
    return () => {
      controller.abort();
    };
  },
  
  getHistory: async (patientId: string) => {
    try {
      if (!isValidObjectId(patientId)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      const response = await api.get(`/ai/chathistory/${patientId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching chat history:', error);
      throw error;
    }
  }
};

export const medicalDocuments = {
  getAll: async (patientId: string) => {
    try {
      if (!isValidObjectId(patientId)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      
      const response = await api.get(`/api/scan/documents/${patientId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching medical documents:', error);
      throw error;
    }
  },
  
  getById: async (patientId: string, documentId: string) => {
    try {
      if (!isValidObjectId(patientId) || !documentId) {
        throw new Error('Invalid ID format');
      }
      
      const response = await api.get(`/api/scan/document/${patientId}/${documentId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching medical document:', error);
      throw error;
    }
  },
  
  delete: async (patientId: string, documentId: string) => {
    try {
      if (!isValidObjectId(patientId) || !documentId) {
        throw new Error('Invalid ID format');
      }
      
      await api.delete(`/api/scan/document/${patientId}/${documentId}`);
      return true;
    } catch (error) {
      console.error('Error deleting medical document:', error);
      return false;
    }
  },

  upload: async (patientId: string, file: File, documentType: string, notes: string = '') => {
    try {
      if (!isValidObjectId(patientId)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('patient_id', patientId);
      
      // Only append document type if it's not empty
      if (documentType) {
        formData.append('document_type', documentType);
      }
      
      if (notes) {
        formData.append('notes', notes);
      }
      
      const response = await api.post('/api/scan/document', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error uploading medical document:', error);
      throw error;
    }
  },
  
  scanNewPatient: async (file: File, notes: string = '') => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      if (notes) {
        formData.append('notes', notes);
      }
      
      const response = await api.post('/api/scannewpatient', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error creating new patient from document:', error);
      throw error;
    }
  },
};

export const patientActivities = {
  getAll: async (patientId: string, filters?: any) => {
    try {
      if (!isValidObjectId(patientId)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      
      const params = new URLSearchParams();
      if (filters?.category) params.append('category', filters.category);
      if (filters?.activity_type) params.append('activity_type', filters.activity_type);
      if (filters?.start_date) params.append('start_date', filters.start_date);
      if (filters?.end_date) params.append('end_date', filters.end_date);
      if (filters?.limit) params.append('limit', filters.limit.toString());
      if (filters?.skip) params.append('skip', filters.skip.toString());
      
      const response = await api.get(`/patients/${patientId}/activities?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching activities:', error);
      throw error;
    }
  },
  
  create: async (patientId: string, activityData: any) => {
    try {
      if (!isValidObjectId(patientId)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      
      const response = await api.post(`/patients/${patientId}/activities`, activityData);
      return response.data;
    } catch (error) {
      console.error('Error creating activity:', error);
      throw error;
    }
  },
  
  uploadBPPhoto: async (patientId: string, file: File, notes?: string) => {
    try {
      if (!isValidObjectId(patientId)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      
      const formData = new FormData();
      formData.append('file', file);
      if (notes) {
        formData.append('notes', notes);
      }
      
      const response = await api.post(`/patients/${patientId}/activities/ocr`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('Error uploading BP photo:', error);
      throw error;
    }
  },
  
  getSummary: async (patientId: string, days: number = 30) => {
    try {
      if (!isValidObjectId(patientId)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      
      const response = await api.get(`/patients/${patientId}/activities/summary?days=${days}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching activity summary:', error);
      throw error;
    }
  },
  
  delete: async (patientId: string, activityId: string) => {
    try {
      if (!isValidObjectId(patientId)) {
        throw new Error('Invalid MongoDB ObjectId format');
      }
      
      await api.delete(`/patients/${patientId}/activities/${activityId}`);
      return true;
    } catch (error) {
      console.error('Error deleting activity:', error);
      return false;
    }
  }
};

export default api; 